import {
  Button,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  PopoverProps,
  styled,
} from '@mui/material';
import React, { useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Divider } from '@components/layout-util-components/divider';
import { PopoverMenuAction } from '@components/popover-menu/use-popover-menu';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH } from '@fortawesome/pro-light-svg-icons';

interface Props {
  actions: Array<PopoverMenuAction>;
  disabled?: boolean;
  className?: string;
  elipsesVariant?: 'one' | 'two';
}

const StyledButton = styled(Button)<{
  elipsesVariant: Props['elipsesVariant'];
}>(({ elipsesVariant, theme: { palette } }) => ({
  display: 'flex',
  width: 32,
  height: 32,
  maxWidth: 32,
  maxHeight: 32,
  minWidth: 32,
  minHeight: 32,
  flex: '0 1 auto',
  color: 'black',
  borderRadius: 4,
  border: '1px solid #C2C9CA',
  background: palette.common.white,
  ...(elipsesVariant === 'two' && {
    background: '#F3F4F7',
    border: 'none',
  }),
}));

const StyledPopover = styled(Popover)<PopoverProps>({
  '&>.MuiPaper-root': {
    minWidth: 150,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
});

export default function ElipsisButton({
  disabled,
  actions,
  className,
  elipsesVariant,
}: Props) {
  const anchorEl = useRef<HTMLButtonElement>();
  const id = useRef(uuid());
  const [open, setOpen] = useState(false);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    anchorEl.current = e.currentTarget;
    setOpen(true);
  };

  const renderAction = (action: PopoverMenuAction) => {
    const key = uuid();

    if ('divider' in action) {
      return <Divider key={key} addMargin superCompact />;
    } else if ('label' in action) {
      const { label, onClick, hasPermission, icon } = action;
      if (hasPermission != null && !hasPermission) {
        return null;
      }

      return (
        <MenuItem
          key={uuid()}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(false);
            onClick();
          }}
        >
          {icon ? <ListItemIcon>{icon}</ListItemIcon> : null}
          {label ? <ListItemText>{label}</ListItemText> : null}
        </MenuItem>
      );
    }
  };

  return (
    <>
      {actions.length > 0 && (
        <>
          <StyledButton
            elipsesVariant={elipsesVariant || 'one'}
            type="button"
            className={className}
            disabled={disabled}
            aria-describedby={id.current}
            onClick={handleClick}
          >
            <FontAwesomeIcon icon={faEllipsisH} />
          </StyledButton>
          <StyledPopover
            id={id.current}
            open={open}
            onClick={(e) => {
              const className = (e.target as any)?.className as
                | string
                | undefined;
              if (
                (className != null &&
                  className.indexOf('MuiBackdrop-root') !== -1) ??
                false
              ) {
                e.stopPropagation();
              }
            }}
            anchorEl={anchorEl.current}
            onClose={() => setOpen(false)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            <MenuList>{actions.map(renderAction)}</MenuList>
          </StyledPopover>
        </>
      )}
    </>
  );
}
